import { useRef } from 'react';
import { AnimatePresence, motion, useIsPresent } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { Direction } from '@/i18n/languages';
import { useIsInitialLoad } from './use-is-initial-load';
import { panelVariants } from './variants';

/**
 * Swaps one panel for another **in place**, with no layout shift.
 *
 * The trick is which panel is in flow: the arriving panel is, and the leaving
 * one is lifted out of it, so the stage's height is always exactly the height
 * of the panel you can act on and nothing above the stage ever moves. Content
 * below it is only ever the end of the card, so a step that is shorter than the
 * last one costs nothing either — which matters, because collapsing reserved
 * space shifts a layout just as badly as inserting content into it.
 *
 * `AnimatePresence` in its default `sync` mode is what keeps the two panels on
 * screen together for the ~160 ms of the crossing; `useIsPresent` inside the
 * panel is what tells the leaving one to get out of the flow. Reduced motion is
 * handled by `MotionConfig` above: the panels cross-fade in place and no part of
 * the geometry changes.
 *
 * Used twice, at two scales: `routes/onboarding-layout` swaps whole screens
 * inside the flow's persistent card, and `/start` swaps its language question
 * for its age question inside one screen.
 */
export function StepStage({
  stepKey,
  direction = 'ltr',
  movement = 'forward',
  travel,
  children,
  className,
}: {
  /** Changing this swaps the panel. Stable within a step. */
  stepKey: string;
  /** Reading direction, so the swap travels the way the language does. */
  direction?: Direction;
  /** Which way the strip moves — `backward` for a step the learner returns to. */
  movement?: 'forward' | 'backward';
  /** Horizontal travel in px. Defaults to the step-panel token. */
  travel?: number;
  children: React.ReactNode;
  className?: string;
}) {
  // The panel the stage mounted with arrives as part of the screen's entrance;
  // every later one is a swap, and travels sideways instead. Once the stage has
  // swapped even once, returning to the panel it mounted with is a swap too —
  // a step you come *back* to should travel back, not rise from nowhere.
  const mountedWith = useRef(stepKey);
  const hasSwapped = useRef(false);

  if (mountedWith.current !== stepKey) {
    hasSwapped.current = true;
  }

  const isMountPanel = mountedWith.current === stepKey && !hasSwapped.current;

  // Same reasoning as the screen's: the panel the server drew is already on
  // screen, so it is not animated in.
  const isInitialLoad = useIsInitialLoad();
  const animateOnMount = !(isMountPanel && isInitialLoad);

  return (
    <div className={cn('relative', className)}>
      <AnimatePresence>
        <Panel
          key={stepKey}
          direction={direction}
          mode={isMountPanel ? 'rise' : 'slide'}
          movement={movement}
          travel={travel}
          animateOnMount={animateOnMount}
        >
          {children}
        </Panel>
      </AnimatePresence>
    </div>
  );
}

function Panel({
  direction,
  mode,
  movement,
  travel,
  animateOnMount,
  children,
}: {
  direction: Direction;
  mode: 'rise' | 'slide';
  movement: 'forward' | 'backward';
  travel: number | undefined;
  animateOnMount: boolean;
  children: React.ReactNode;
}) {
  const isPresent = useIsPresent();

  return (
    <motion.div
      variants={panelVariants({ direction, mode, movement, travel })}
      initial={animateOnMount ? 'hidden' : false}
      animate="shown"
      exit="gone"
      // For the ~160 ms it is leaving, this panel is a ghost: out of the flow,
      // and reachable by neither a pointer nor the keyboard.
      style={isPresent ? undefined : { position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0, top: 0 }}
      className={isPresent ? undefined : 'pointer-events-none'}
      aria-hidden={!isPresent}
      inert={!isPresent}
    >
      {children}
    </motion.div>
  );
}
